import React,{useEffect,useMemo,useRef,useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {platformDetails} from './PlatformBadges';
import './PublishModal.css';

const {
  FiArrowRight,
  FiCheck,
  FiLoader,
  FiSettings,
  FiX
}=FiIcons;

const platformOrder=['instagram','facebook','linkedin','tiktok'];

function PlatformOption({
  id,
  connection,
  selected,
  status,
  disabled,
  onToggle,
  onConnect
}) {
  const platform=platformDetails[id];
  const connected=Boolean(connection?.connected);
  const state=status || 'idle';

  return (
    <div
      className={`publish-platform ${connected ? 'connected' : 'disconnected'} ${selected ? 'selected' : ''}`}
    >
      <button
        type="button"
        className="publish-platform-select"
        disabled={!connected || disabled}
        onClick={()=> onToggle(id)}
      >
        <span className="publish-platform-icon">
          <SafeIcon icon={platform.icon} />
        </span>
        <span className="publish-platform-copy">
          <strong>{platform.label}</strong>
          <small>
            {connected
              ? connection.handle || 'Connected'
              : 'Not connected'}
          </small>
        </span>
        <span className={`publish-platform-state ${state}`}>
          {state==='publishing' && (
            <SafeIcon icon={FiLoader} className="publish-spinner" />
          )}
          {state==='success' && <SafeIcon icon={FiCheck} />}
          {state==='idle' && connected && (
            <i aria-hidden="true">{selected && <SafeIcon icon={FiCheck} />}</i>
          )}
        </span>
      </button>

      {!connected && (
        <button
          type="button"
          className="connect-first"
          disabled={disabled}
          onClick={onConnect}
        >
          Connect first
          <SafeIcon icon={FiArrowRight} />
        </button>
      )}
    </div>
  );
}

function PublishModal({
  post,
  brand,
  connections,
  onClose,
  onConnect,
  onPublished
}) {
  const [selected,setSelected]=useState([]);
  const [statuses,setStatuses]=useState({});
  const [phase,setPhase]=useState('select');
  const timers=useRef([]);
  const connectedIds=useMemo(
    ()=> platformOrder.filter((id)=> connections[id]?.connected),
    [connections]
  );

  useEffect(()=> {
    return ()=> {
      timers.current.forEach((timer)=> window.clearTimeout(timer));
    };
  },[]);

  const togglePlatform=(id)=> {
    setSelected((current)=> (
      current.includes(id)
        ? current.filter((item)=> item!==id)
        : [...current,id]
    ));
  };

  const connectPlatform=()=> {
    if (phase!=='select') {
      return;
    }

    onClose();
    onConnect();
  };

  const publish=()=> {
    if (!selected.length || phase!=='select') {
      return;
    }

    setPhase('publishing');
    setStatuses(Object.fromEntries(
      selected.map((id)=> [id,'publishing'])
    ));

    selected.forEach((id,index)=> {
      const timer=window.setTimeout(()=> {
        setStatuses((current)=> ({...current,[id]:'success'}));
      },700+(index*380));

      timers.current.push(timer);
    });

    const completeTimer=window.setTimeout(()=> {
      setPhase('success');
      onPublished(post.id,selected);
    },900+(selected.length*380));

    timers.current.push(completeTimer);
  };

  const selectedLabels=selected
    .map((id)=> platformDetails[id]?.label)
    .filter(Boolean);

  return (
    <div
      className="modal-backdrop"
      onMouseDown={phase==='select' ? onClose : undefined}
    >
      <motion.section
        className="publish-modal"
        onMouseDown={(event)=> event.stopPropagation()}
        initial={{opacity:0,scale:0.96,y:10}}
        animate={{opacity:1,scale:1,y:0}}
      >
        {phase==='select' && (
          <button
            type="button"
            className="publish-close"
            aria-label="Close publishing dialog"
            onClick={onClose}
          >
            <SafeIcon icon={FiX} />
          </button>
        )}

        <span className="eyebrow">
          {phase==='success' ? 'Published' : 'Connect & publish'}
        </span>
        <h2>
          {phase==='success'
            ? 'Your post is live.'
            : 'Choose where to publish.'}
        </h2>
        <p className="publish-description">
          {phase==='success'
            ? `Published to ${selectedLabels.join(', ')}.`
            : post.headline}
        </p>

        <div className="publish-platform-list">
          {platformOrder.map((id)=> (
            <PlatformOption
              key={id}
              id={id}
              connection={connections[id]}
              selected={selected.includes(id)}
              status={statuses[id]}
              disabled={phase!=='select'}
              onToggle={togglePlatform}
              onConnect={connectPlatform}
            />
          ))}
        </div>

        {!connectedIds.length && phase==='select' && (
          <div className="publish-connect-notice">
            <SafeIcon icon={FiSettings} />
            <div>
              <strong>Connect a platform first</strong>
              <p>
                Add a publishing destination in Settings before continuing.
              </p>
            </div>
          </div>
        )}

        <div className="publish-actions">
          {phase==='select' && (
            <>
              <button
                type="button"
                className="text-button"
                onClick={onClose}
              >
                Cancel
              </button>
              {connectedIds.length ? (
                <button
                  type="button"
                  className="primary-button"
                  disabled={!selected.length}
                  style={{background:brand.primary}}
                  onClick={publish}
                >
                  Publish
                  <SafeIcon icon={FiArrowRight} />
                </button>
              ) : (
                <button
                  type="button"
                  className="primary-button"
                  style={{background:brand.primary}}
                  onClick={connectPlatform}
                >
                  Open connections
                  <SafeIcon icon={FiArrowRight} />
                </button>
              )}
            </>
          )}

          {phase==='publishing' && (
            <span className="publish-progress">
              <SafeIcon icon={FiLoader} className="publish-spinner" />
              Publishing your post…
            </span>
          )}

          {phase==='success' && (
            <button
              type="button"
              className="primary-button"
              style={{background:brand.primary}}
              onClick={onClose}
            >
              <SafeIcon icon={FiCheck} />
              Done
            </button>
          )}
        </div>

        <small className="publish-mock-note">
          Frontend preview · No content is sent to social platforms
        </small>
      </motion.section>
    </div>
  );
}

export default PublishModal;